import { api } from "../services/api";

export type DirectNoticeFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

export type DirectNoticePayload = {
  title: string;
  body: string;
  uploadedFiles?: DirectNoticeFile[];
};

/**
 * A send that failed part-way. The notices in `noticeIds` were already created
 * and reached their parents, so callers can still record them.
 */
export class DirectNoticesError extends Error {
  constructor(
    message: string,
    readonly noticeIds: string[],
    options?: { cause?: unknown }
  ) {
    super(message, options);
    this.name = "DirectNoticesError";
  }
}

/**
 * Sends one direct notice to each User and returns the created notice IDs,
 * so callers can delete or track them later. A response without an ID is
 * skipped (and logged) rather than stopping the remaining sends. A failed
 * create stops the sending with a DirectNoticesError that keeps the original
 * message and the IDs created before it.
 */
export async function sendDirectNotices(
  recipientUserIds: string[],
  payload: DirectNoticePayload,
  token: string
): Promise<string[]> {
  if (recipientUserIds.length === 0) {
    throw new Error("There's no one to send this to.");
  }

  const noticeIds: string[] = [];
  for (const targetUserId of recipientUserIds) {
    let created;
    try {
      created = await api.notices.create(
        {
          title: payload.title,
          body: payload.body,
          type: "direct",
          targetUserId,
          isUrgent: false,
          uploadedFiles: payload.uploadedFiles,
        },
        token
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Couldn't send the notice.";
      throw new DirectNoticesError(message, noticeIds, { cause: error });
    }
    const noticeId = created?.notice?.noticeId;
    if (noticeId) {
      noticeIds.push(noticeId);
    } else {
      console.warn("Direct notice created without a notice ID", { targetUserId });
    }
  }
  return noticeIds;
}
